import { useState } from 'react';
import { Alert, Button, Drawer, Group, Stack, Text, Title } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useAdminResetPassword } from '../../queries/admin-users';
import { NewUserCredentialsPanel } from './NewUserCredentialsPanel';
import { ApiError } from '../../lib/api-error';
import type { User } from '../../types/api';

interface Props {
  opened: boolean;
  onClose: () => void;
  user: User | null;
}

export function ResetPasswordDrawer({ opened, onClose, user }: Props) {
  const resetM = useAdminResetPassword();
  const [credentials, setCredentials] = useState<{ user: User; temp: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [lastOpened, setLastOpened] = useState(opened);

  // Reset panel state whenever the drawer transitions to "opened" without useEffect.
  if (lastOpened !== opened) {
    setLastOpened(opened);
    if (opened) setCredentials(null);
  }

  if (!user) return null;

  async function onConfirm() {
    if (!user) return;
    setSubmitting(true);
    try {
      const res = await resetM.mutateAsync(user.id);
      setCredentials({ user: res.user, temp: res.temp_password });
    } catch (err) {
      notifications.show({
        title: 'Reset failed',
        message: err instanceof ApiError ? err.message : 'Unexpected error',
        color: 'red',
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="md"
      padding="md"
      title={<Title order={4}>Reset password for {user.username}</Title>}
    >
      {credentials ? (
        <NewUserCredentialsPanel
          user={credentials.user}
          tempPassword={credentials.temp}
          heading="Password reset"
          onDone={() => {
            setCredentials(null);
            onClose();
          }}
        />
      ) : (
        <Stack gap="md">
          <Alert color="yellow" variant="light" icon={<IconAlertTriangle size={18} />}>
            This rotates <strong>{user.username}</strong>'s password to a new random value and signs
            them out of every active session. You will see the new password once and must share it
            through a secure channel.
          </Alert>
          <Text size="sm" c="dimmed">
            The user will be required to set a new password when they sign in.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose} type="button">
              Cancel
            </Button>
            <Button color="red" onClick={onConfirm} loading={submitting}>
              Reset password
            </Button>
          </Group>
        </Stack>
      )}
    </Drawer>
  );
}
